<template>
	<div :key="downloadsCounter">
		<div v-if="showCalcButton && !isRepairExtensionRejected" class="m-4 text-left flex">
			<DocumentFileUploader
				ref="DocumentFileUploader"
				:instant-upload="false"
				class="cursor-pointer mr-2"
				accept="application/zip, application/x-zip"
				:handle-on-click="false"
				:document="documentZip"
				:repair-extension-id="repairExtension.id"
				@input="updateDocumentZip"
			>
				<BaseButton @click="askForZipOverwrite">
					{{ $t('upload_repair_extension_zip', { index: realIndex }) }}
					<font-awesome-icon class="text-green-300" :icon="['fas', 'check']" v-if="repairExtension.zip != null"></font-awesome-icon>
				</BaseButton>
			</DocumentFileUploader>
			<DocumentFileUploader
				ref="DocumentFileUploaderPdf"
				:instant-upload="false"
				class="cursor-pointer mr-2"
				accept="application/pdf"
				:handle-on-click="false"
				:document="documentPdf"
				:repair-extension-id="repairExtension.id"
				@input="updateDocumentPdf"
			>
				<BaseButton @click="askForPDFOverwrite">
					{{ $t('upload_repair_extension_pdf', { index: realIndex }) }}
					<font-awesome-icon class="text-green-300" :icon="['fas', 'check']" v-if="repairExtension.pdf != null"></font-awesome-icon>
				</BaseButton>
			</DocumentFileUploader>
			<router-link
				:to="{
					name: 'damagecases.edit.repairExtensions.mail',
					params: { id: $route.params.processId || $route.params.id, extensionID: repairExtension.id },
				}"
				v-if="isLawyerOfficeSet && repairExtension.status === 'approved' && (repairExtension.pdf != null || repairExtension.zip != null)"
			>
				<BaseButton>
					{{ $t('notify_lawyer') }}
				</BaseButton>
			</router-link>
		</div>
		<div class="text-left mt-4 ml-8 flex justify-between mr-8 items-center">
			<h3 class="font-bold text-lg" v-if="repairExtension.isNew">{{ getStringFromDate(repairExtension.date) }}</h3>
			<div class="font-bold text-lg" v-if="!repairExtension.isNew">
				{{ getStringFromDate(repairExtension.date) }} - {{ getTimeFromDate(repairExtension.date) }}
			</div>

			<div class="flex">
				<div class="font-bold text-lg mr-4">{{ $t('repair_extension_index', { index: realIndex }) }}</div>

				<div v-if="isRepairExtensionApproved && isUserAllowedInLevel5or6 && $store.getters.isProcessLiability" class="text-green-300">
					<font-awesome-icon :icon="['fas', 'thumbs-up']"></font-awesome-icon>
				</div>
				<div v-if="isRepairExtensionRejected && isUserAllowedInLevel5or6 && $store.getters.isProcessLiability" class="text-red-300">
					<font-awesome-icon :icon="['fas', 'thumbs-down']"></font-awesome-icon>
				</div>
				<div v-if="!isRepairExtensionRejected && !isRepairExtensionApproved && isUserAllowedInLevel5or6 && $store.getters.isProcessLiability">
					<font-awesome-icon :icon="['fas', 'question']"></font-awesome-icon>
				</div>
			</div>
		</div>

		<div class="mx-8 border-2 mt-4"></div>

		<div class="text-left mt-8 ml-8 flex justify-between flex-wrap flex-col md:flex-row mb-4">
			<div class="mr-8 w-48 flex-grow mb-4 md:mb-0">
				<h3 class="font-bold">{{ $t('images') }}</h3>
				<div v-if="!repairExtension.imageFiles || !repairExtension.imageFiles.length">
					{{ $t('no_images_uploaded') }}
				</div>
			</div>

			<div class="mr-8 flex" v-if="isUserAllowedInLevel2">
				<BaseButton class="self-end mr-4" :disabled="!enableDownloadImageFiles" @click="getDownloadURL">{{
					$t('download_selected_images')
				}}</BaseButton>
			</div>
		</div>

		<BaseCamera
			v-if="isUserAllowedInLevel5or6 && isRepairExtensionRequested && !isExtensionClosed"
			class="ml-8 mt-4 text-left mr-8"
			:disable-success-message="true"
			:enable-preview="false"
			:allow-upload-in-app-container="false"
			@snapshot="addImageFile($event, repairExtension)"
		/>

		<div class="mt-4 ml-8 mr-8" v-if="repairExtension.imageFiles">
			<div class="grid grid-cols-2 xl:grid-cols-3 gap-8">
				<div v-for="(metaImage, key) in repairExtension.imageFiles" :key="metaImage.id">
					<MetaInfoImage
						:meta-info-media="metaImage"
						v-model="metaImage.isSelected"
						@delete="removeImageFile(key)"
						@input="checkIsFileSelected"
						:chooseable="!isUserAllowedInLevel5or6"
						:deletable="isUserAllowedInLevel5or6 && isRepairExtensionRequested && !isExtensionClosed"
					/>
				</div>
			</div>
		</div>

		<div class="mx-8 border-2 mt-8"></div>

		<div class="text-left m-8">
			<BaseInput :label="$t('description_input')">
				<textarea
					class="resize-y w-full shadow-m h-32"
					v-model="repairExtension.description"
					:disabled="!isUserAllowedInLevel5or6 || !isRepairExtensionRequested || isExtensionClosed"
				></textarea>
			</BaseInput>
		</div>

		<div class="mx-8 border-2 mt-8" v-if="isUserAllowedInLevel2 || repairExtension.damageAssessorComment"></div>

		<div class="text-left m-8" v-if="isUserAllowedInLevel2 || repairExtension.damageAssessorComment">
			<BaseInput ref="damageAssessorComment" :label="$t('damage_assessor_notes_input')" :error-message="$t('explain_decision')">
				<textarea
					class="resize-y w-full shadow-m h-32"
					v-model="repairExtension.damageAssessorComment"
					:disabled="isUserAllowedInLevel5or6"
				></textarea>
			</BaseInput>
		</div>

		<div class="flex justify-end">
			<div class="flex mx-8 mt-8 text-right" v-if="isUserAllowedInLevel2">
				<div class="cursor-pointer btn-default mr-3" @click="allowRepairExtension()" :class="{ 'text-green-300': isRepairExtensionApproved }">
					<font-awesome-icon :icon="['fas', 'thumbs-up']"></font-awesome-icon>
				</div>

				<div class="cursor-pointer btn-default" @click="denyRepairExtension()" :class="{ 'text-red-300': isRepairExtensionRejected }">
					<font-awesome-icon :icon="['fas', 'thumbs-down']"></font-awesome-icon>
				</div>
			</div>
			<div class="text-right mx-8 mt-8" v-if="isUserAllowedInLevel5or6 && !isExtensionClosed">
				<BaseButton @click="save(true)" class="btn-blue">{{ getSaveButtonText }}</BaseButton>
			</div>
		</div>
	</div>
</template>

<script>
import apiDefaults from '@/api/defaultConfiguration';
import { getStringFromDate, getTimeFromDate, getUnixTimestamp } from '@/plugins/Utils';
import { v4 } from 'uuid';
import { DamagecasesApiFactory, MetainfofileApiFactory } from '@/api/generated';
import MetaInfoImage from '@/components/damagecases/forms/MetaInfoImage';
import BaseButton from '@/components/forms/buttons/BaseButton';
import BaseCamera from '@/components/forms/inputs/BaseCamera';
import BaseInput from '@/components/forms/inputs/BaseInput';
import LoadingSpinner from '@/mixins/LoadingSpinner';
import Authentication from '@/mixins/Authentication';
import { MetaInfoFileTypeEnum, RepairExtensionStatusEnum } from '@/api/generated/models';
import DocumentFileUploader from '@/components/forms/inputs/DocumentFileUploader';
import RepairExtensionsRepository from '@/api/repositories/RepairExtensionsRepository';

export default {
	name: 'RepairExtension',
	components: { DocumentFileUploader, BaseInput, BaseCamera, BaseButton, MetaInfoImage },
	mixins: [LoadingSpinner, Authentication],
	props: {
		repairExtension: {
			required: true,
		},
		index: {},
		realIndex: {},
		zip_uploaded: { default: false },
		pdf_uploaded: { default: false },
	},
	data() {
		return {
			downloadsCounter: 0,
			enableDownloadImageFiles: false,
			isSomethingChange: false,
			documentPdf: { type: 'reparaturerweiterung_pdf' },
			documentZip: { type: 'reparaturerweiterung_zip' },
		};
	},
	computed: {
		showCalcButton() {
			return this.getAuthenticatedUserGroupLevel === '2' && this.repairExtension.status === RepairExtensionStatusEnum.Approved;
		},
		isLawyerOfficeSet() {
			return !!this.$store.getters.getDamageCase?.lawyerOffice?.id;
		},
		isExtensionClosed() {
			return !this.repairExtension.isNew && this.isUserAllowedInLevel5or6;
		},
		getSaveButtonText() {
			return this.getAuthenticatedUserGroupLevel === '2' || !this.repairExtension.isNew
				? this.$t('general.save')
				: this.$t('apply_repair_extension_short');
		},
		getAuthenticatedUserGroupLevel() {
			return this.$store.getters.getAuthenticatedUserGroupLevel;
		},
		isRepairExtensionApproved() {
			return this.repairExtension.status === RepairExtensionStatusEnum.Approved;
		},
		isRepairExtensionRejected() {
			return this.repairExtension.status === RepairExtensionStatusEnum.Rejected;
		},
		isRepairExtensionRequested() {
			return this.repairExtension.status === RepairExtensionStatusEnum.Requested;
		},
	},
	methods: {
		askForZipOverwrite() {
			if (this.repairExtension.zip != null) {
				this.$swal({
					title: this.$t('sure').toString(),
					text: this.$t('repair_extension_calculation_file_overwrite').toString(),
					icon: 'warning',
					cancelButtonText: this.$t('general.cancel').toString(),
					showCancelButton: true,
					confirmButtonText: this.$t('overwrite').toString(),
				}).then(result => {
					if (result.value) {
						this.$refs.DocumentFileUploader.openUpload();
					}
				});
			} else {
				this.$refs.DocumentFileUploader.openUpload();
			}
		},
		askForPDFOverwrite() {
			if (this.repairExtension.pdf != null) {
				this.$swal({
					title: this.$t('sure').toString(),
					text: this.$t('repair_extension_calculation_file_overwrite').toString(),
					icon: 'warning',
					cancelButtonText: this.$t('general.cancel').toString(),
					showCancelButton: true,
					confirmButtonText: this.$t('overwrite').toString(),
				}).then(result => {
					if (result.value) {
						this.$refs.DocumentFileUploaderPdf.openUpload();
					}
				});
			} else {
				this.$refs.DocumentFileUploaderPdf.openUpload();
			}
		},
		updateDocumentZip($event) {
			this.save();
			this.$refs.DocumentFileUploader.triggerFileUpload()
				.then(response => {
					this.$emit('updateDocumentZip', $event);
					this.$swal(this.$t('general.success').toString(), this.$t('file_uploaded_repair_extension_approved').toString(), 'success');
				})
				.catch(() => {
					this.$swal(this.$t('general.error').toString(), this.$t('file_not_found_zip').toString(), 'error');
				});
		},
		updateDocumentPdf($event) {
			this.save();
			this.$refs.DocumentFileUploaderPdf.triggerFileUpload().then(response => {
				this.$emit('updateDocumentPdf', $event);
				this.$swal(this.$t('general.success').toString(), this.$t('file_uploaded_repair_extension_approved').toString(), 'success');
			});
		},
		checkIsFileSelected() {
			this.enableDownloadImageFiles = undefined;
			this.enableDownloadImageFiles = this.isOneFileSelected(this.repairExtension.imageFiles);
		},
		submitImage(repairExtension, imageFile) {
			return DamagecasesApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.damagecasesIdRepairextensionsExtidMediafilesPost(
					parseInt(this.$route.params.processId || this.$route.params.id),
					repairExtension.id,
					imageFile,
				)
				.then(result => {
					imageFile.id = result.data.id;
					imageFile.timestamp = result.data.timestamp;
					imageFile.isNotUploaded = false;
				});
		},
		denyRepairExtension() {
			if (this.repairExtension.damageAssessorComment) {
				this.repairExtension.status = RepairExtensionStatusEnum.Rejected;
				this.$emit('statusSet', this.repairExtension.status);
				this.save();
				this.$refs.damageAssessorComment.isValid = true;
			} else {
				this.$swal(this.$t('general.attention').toString(), this.$t('status_not_changed_explain_decision').toString(), 'warning');
				this.$refs.damageAssessorComment.isValid = false;
			}
		},
		allowRepairExtension() {
			this.repairExtension.status = RepairExtensionStatusEnum.Approved;
			this.$emit('statusSet', this.repairExtension.status);
		},
		getDownloadURL() {
			const ids = [];
			const array = this.repairExtension.imageFiles;

			for (let i = 0; i < array.length; i++) {
				const file = array[i];

				if (file.id && !file.isNotUploaded && file.isSelected) {
					ids.push('mediaIds[]=' + file.id);
				}
			}

			const date = new Date();
			const year = new Intl.DateTimeFormat('en', { year: 'numeric' }).format(date);
			const month = new Intl.DateTimeFormat('en', { month: '2-digit' }).format(date);
			const day = new Intl.DateTimeFormat('en', { day: '2-digit' }).format(date);
			const dateString = `${year}${month}${day}`;

			const mediaIdQueryString = ids.join('&');
			let url = '';
			new RepairExtensionsRepository()
				.downloadRepairExtensionMediaFiles(parseInt(this.$route.params.processId), this.repairExtension.id, mediaIdQueryString)
				.then(response => {
					const fileName = 'Reparaturerweiterung#' + this.realIndex + '_' + dateString + '.zip';
					const blob = new Blob([response.data]);
					url = window.URL.createObjectURL(blob);
					setTimeout(function() {
						const a = document.createElement('a');
						a.style.display = 'none';
						a.href = url;
						a.download = fileName;
						document.body.appendChild(a);
						a.click();
						window.URL.revokeObjectURL(url);
					}, 200);
				})
				.catch(e => {
					console.log(e);
					this.$swal(this.$t('general.error').toString(), this.$t('media_cannot_be_downloaded').toString(), 'error');
				});
		},
		isOneFileSelected(files) {
			return files.find(element => element.isSelected) !== undefined;
		},
		getStringFromDate(string) {
			return getStringFromDate(new Date(string * 1000));
		},
		getTimeFromDate(string) {
			return getTimeFromDate(new Date(string * 1000));
		},
		submitDeleteMetaFiles() {
			const array = [];
			this.repairExtension.deletedMetaFiles?.forEach((file, index) => {
				if (!file.isNotUploaded) {
					const promise = MetainfofileApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios).metainfofileIdDelete(
						file.id,
						'repairExtension',
					);
					array.push(promise);
				}
			});
			this.repairExtension.deletedMetaFiles = undefined;
			return array;
		},
		addImageFile(image) {
			this.setLoadingStatus(true);
			MetainfofileApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.metainfofileTmpimageuploadPost({ type: 'image', base64Content: image })
				.then(result => {
					this.repairExtension.imageFiles.unshift({
						id: v4(),
						timestamp: result.data.timestamp,
						type: MetaInfoFileTypeEnum.Image,
						base64Content: result.data.base64Content,
						isNotUploaded: true,
					});
					this.isSomethingChange = true;
					this.downloadsCounter++;
					this.setLoadingStatus(false);
				})
				.catch(error => {
					this.setLoadingStatus(false);
					this.$swal(this.$t('general.attention').toString(), this.$t('please_check_input').toString(), 'warning');
				});
		},
		removeImageFile(index) {
			const file = this.repairExtension.imageFiles.splice(index, 1)[0];
			this.removeMetaFile(file);
		},
		removeMetaFile(file) {
			if (!file.isNotUploaded) {
				if (!this.repairExtension.deletedMetaFiles) {
					this.repairExtension.deletedMetaFiles = [];
				}
				this.repairExtension.deletedMetaFiles.push(file);
			}
			this.downloadsCounter++; // TODO FIND A BETTER WAY TO UPDATE UI
		},
		updateRepairExtension() {
			const tmpRepairExtension = { ...this.repairExtension, imageFiles: [] };
			return DamagecasesApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios).damagecasesIdRepairextensionsExtidPut(
				parseInt(this.$route.params.processId || this.$route.params.id),
				this.repairExtension.id,
				tmpRepairExtension,
			);
		},
		updateImageFiles() {
			const array = [];
			this.repairExtension.imageFiles
				.slice()
				.reverse()
				.forEach((imageFile, index) => {
					if (imageFile.isNotUploaded) {
						array.push(this.submitImage(this.repairExtension, imageFile));
					}
				});
			return array;
		},
		save(emit = true) {
			if (this.repairExtension.isNotUploaded) {
				if (!this.repairExtension.description) {
					this.$swal(this.$t('general.attention').toString(), this.$t('enter_text_in_description').toString(), 'warning');
					return;
				}
				this.setLoadingStatus(true);
				this.repairExtension.date = getUnixTimestamp();
				DamagecasesApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
					.damagecasesIdRepairextensionsPost(parseInt(this.$route.params.processId || this.$route.params.id), this.repairExtension)
					.then(result => {
						this.repairExtension.id = result.data.id;
						this.repairExtension.isNotUploaded = false;
						this.upload();
						if (emit) {
							this.$emit('saveExtension', true);
							this.repairExtension.isNew = false;
						}
					})
					.catch(error => {
						this.setLoadingStatus(false);
						this.$swal(this.$t('general.error').toString(), this.$t('upload_repair_extension_error').toString(), 'error');
					});
			} else {
				this.setLoadingStatus(true);
				this.upload();
				this.$emit('saveExtension', true);
			}
		},
		upload() {
			const array = [];
			array.push(this.updateRepairExtension());
			array.concat(this.updateImageFiles());
			array.concat(this.submitDeleteMetaFiles());
			return Promise.all(array)
				.then(result => {
					this.isSomethingChange = false;
					this.$store.commit('setRepairExtensionNeedUpdate', true);
					this.setLoadingStatus(false);
					if (!this.isUserAllowedInLevel2) {
						this.$swal(this.$t('general.success').toString(), this.$t('repair_extension_applied').toString(), 'success');
					}
				})
				.catch(error => {
					this.setLoadingStatus(false);
					this.$swal(this.$t('general.attention').toString(), this.$t('please_check_input').toString(), 'warning');
				});
		},
	},
};
</script>

<style scoped></style>
